import React, { useEffect, useRef, useState } from 'react';
import { cleanBassNotes, loadBassCatalog, midiFrequency, renderBass } from './bassVoice';
import { bassVariation, editBassNote, glideInto, noteMidi, setNotePitch, transposeBassOctave, glidePreviewNotes } from './bassEditing';
import './bassEditor.css';
const names = ['C', 'C#', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B'];
export const bassNoteName = pitch => `${names[(pitch + 120) % 12]}${Math.floor(pitch / 12) - 1}`;
export function bassKey(value) {
  const m = /^([A-G])([#b]?)\s*(maj(?:or)?|min(?:or)?)$/i.exec(String(value || '').trim());
  if (!m) return null;
  let root = {
    C: 0,
    D: 2,
    E: 4,
    F: 5,
    G: 7,
    A: 9,
    B: 11
  }[m[1].toUpperCase()] + (m[2] === '#' ? 1 : m[2] === 'b' ? -1 : 0);
  root = (root + 12) % 12;
  const mode = m[3].toLowerCase().startsWith('maj') ? 'major' : 'minor';
  return {
    root,
    mode,
    scale: (mode === 'major' ? [0, 2, 4, 5, 7, 9, 11] : [0, 2, 3, 5, 7, 8, 10]).map(n => (n + root) % 12)
  };
}
export function useBassPreview(getContext, destination, onStopArrangement) {
  const source = useRef(null),
    ticket = useRef(0),
    timer = useRef(null);
  const [playing, setPlaying] = useState(false),
    [error, setError] = useState('');
  const stop = () => {
    ticket.current++;
    clearTimeout(timer.current);
    try {
      source.current?.stop();
    } catch (_) {}
    source.current = null;
    setPlaying(false);
  };
  useEffect(() => () => {
    ticket.current++;
    clearTimeout(timer.current);
    try {
      source.current?.stop();
    } catch (_) {}
  }, []);
  const play = async (value, bpm, loop = false) => {
    stop();
    onStopArrangement?.();
    const id = ticket.current;
    setError('');
    try {
      const ctx = getContext();
      await ctx.resume();
      const buffer = await renderBass({
        ...value,
        bpm
      });
      if (id !== ticket.current) return;
      const node = ctx.createBufferSource();
      node.buffer = buffer;
      node.loop = loop;
      node.connect(destination ? destination() : ctx.destination);
      source.current = node;
      node.start();
      setPlaying(true);
      if (!loop) timer.current = setTimeout(() => {
        if (id === ticket.current) {
          source.current = null;
          setPlaying(false);
        }
      }, buffer.duration * 1000 + 20);
    } catch (e) {
      if (id === ticket.current) setError(e.message || 'Could not play bass. Try again.');
    }
  };
  return {
    play,
    stop,
    playing,
    error
  };
}
export function KeyLearning({
  keySignature,
  getContext,
  destination,
  onStopArrangement,
  portrait = '/pizzul/writing.png'
}) {
  const [open, setOpen] = useState(false);
  const key = bassKey(keySignature);
  const preview = useBassPreview(getContext, destination, onStopArrangement);
  if (!key) return <span className="bass-key-unknown">Choose a song key to hear major and minor.</span>;
  const hear = mode => {
    const root = 48 + key.root;
    preview.play({
      bars: 1,
      volume: .45,
      pattern: [0, mode === 'major' ? 4 : 3, 7, 0].map((n, i) => ({
        step: i * 4,
        frequency: midiFrequency(root + n),
        durationSteps: 3,
        velocity: .7,
        glideMs: 0
      }))
    }, 100);
  };
  return <div className="bass-learning"><button aria-expanded={open} onClick={() => {
      setOpen(!open);
      preview.stop();
    }}>Your beat: {key.mode === 'major' ? 'Major' : 'Minor'} · Hear the difference</button>{open && <div className="bass-lesson"><img src={portrait} alt="Pizzul" /><div><p>Same rhythm, different feeling. Major and minor change the notes around Home. Either can tell any kind of story. Think of a song you know: which version feels closer?</p><button onClick={() => hear('major')}>Hear major</button><button onClick={() => hear('minor')}>Hear minor</button>{preview.playing && <button onClick={preview.stop}>Stop example</button>}<small>Separate example. Your beat stays in its original key.</small>{preview.error && <span role="alert">{preview.error}</span>}</div></div>}</div>;
}
const MAX = 72,
  MIN = 12,
  ROW = 20,
  BEAT = 44;
export default function BassEditor({
  value,
  onChange: commitValue,
  onUndoEdit,
  onRedoEdit,
  canUndoEdit,
  canRedoEdit,
  onClose,
  bpm,
  keySignature,
  getContext,
  destination,
  onListen,
  onStopArrangement,
  portrait = '/pizzul/writing.png',
  onGenerate
}) {
  const history = useRef([]),
    future = useRef([]);
  const onChange = next => {
    if (!onUndoEdit) history.current.push(structuredClone(value));
    future.current = [];
    commitValue(next);
  };
  const undo = () => {
    if (onUndoEdit) {
      preview.stop();
      onUndoEdit();
      return;
    }
    if (!history.current.length) return;
    preview.stop();
    future.current.push(structuredClone(value));
    commitValue(history.current.pop());
  };
  const redo = () => {
    if (onRedoEdit) {
      preview.stop();
      onRedoEdit();
      return;
    }
    if (!future.current.length) return;
    preview.stop();
    history.current.push(structuredClone(value));
    commitValue(future.current.pop());
  };
  const notes = cleanBassNotes(value.pattern, value.bars || 4);
  const [activePitch, setActivePitch] = useState(null);
  const [draft, setDraft] = useState(notes),
    [selected, setSelected] = useState(null),
    [snap, setSnap] = useState(1),
    [lock, setLock] = useState(value.lockToKey !== false),
    [catalog, setCatalog] = useState([]),
    [genre, setGenre] = useState(''),
    [error, setError] = useState(''),
    [loading, setLoading] = useState(true),
    [candidate, setCandidate] = useState(null);
  const drag = useRef(null),
    grid = useRef(null),
    scroll = useRef(null),
    local = useRef(draft);
  const key = bassKey(keySignature);
  const bars = value.bars || 4,
    total = bars * 16,
    width = bars * 4 * BEAT;
  const preview = useBassPreview(getContext, destination, onStopArrangement);
  useEffect(() => {
    const n = cleanBassNotes(value.pattern, bars);
    setDraft(n);
    local.current = n;
    setCandidate(null);
  }, [value.pattern, bars]);
  const load = () => {
    setLoading(true);
    setError('');
    loadBassCatalog().then(c => setCatalog(c.sounds || [])).catch(e => setError(e.message)).finally(() => setLoading(false));
  };
  useEffect(() => {
    let alive = true;
    loadBassCatalog().then(c => {
      if (alive) setCatalog(c.sounds || []);
    }).catch(e => {
      if (alive) setError(e.message);
    }).finally(() => {
      if (alive) setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, []);
  const initialTop = useRef((() => {
    const pitches = notes.map(noteMidi).sort((a, b) => a - b);
    return MAX - Math.min(MAX, (pitches[0] || 24) + 4);
  })());
  useEffect(() => {
    if (scroll.current) scroll.current.scrollTop = initialTop.current * ROW;
  }, []);
  const update = n => {
    local.current = n;
    setDraft(n);
  };
  const commit = n => {
    preview.stop();
    setCandidate(null);
    onChange({
      ...value,
      pattern: cleanBassNotes(n, total / 16),
      lockToKey: lock
    });
  };
  const fold = p => {
    p = Math.max(MIN, Math.min(MAX, p));
    if (!lock || !key) return p;
    for (let d = 0; d < 12; d++) {
      if (key.scale.includes((p - d + 120) % 12) && p - d >= MIN) return p - d;
      if (key.scale.includes((p + d) % 12) && p + d <= MAX) return p + d;
    }
    return p;
  };
  const step = x => Math.max(0, Math.min(total - .25, Math.round(x / BEAT * 4 / snap) * snap));
  const audition = n => {
    setActivePitch(noteMidi(n));
    return preview.play({
      ...value,
      pattern: [{
        ...n,
        step: 0,
        durationSteps: Math.min(n.durationSteps, 4),
        glideMs: 0,
        slide: false
      }],
      bars: 1
    }, bpm);
  };
  const begin = (e, n, resize = false) => {
    if (e.button !== 0 || candidate) return;
    e.preventDefault();
    e.stopPropagation();
    grid.current?.focus();
    setSelected(n.id);
    setActivePitch(noteMidi(n));
    drag.current = {
      n,
      resize,
      x: e.clientX,
      y: e.clientY,
      original: local.current
    };
  };
  const add = e => {
    if (e.button !== 0 || candidate) return;
    e.preventDefault();
    const r = grid.current.getBoundingClientRect();
    const pitch = fold(MAX - Math.floor((e.clientY - r.top) / ROW));
    const n = setNotePitch({
      id: `note-${crypto.randomUUID()}`,
      step: step(e.clientX - r.left),
      durationSteps: snap,
      velocity: .8,
      glideMs: 0,
      slide: false
    }, pitch);
    const next = [...draft, n];
    update(next);
    setSelected(n.id);
    commit(next);
    audition(n);
  };
  useEffect(() => {
    const move = e => {
      const d = drag.current;
      if (!d) return;
      const delta = Math.round((e.clientX - d.x) / BEAT * 4 / snap) * snap;
      let patch;
      if (d.resize) patch = {
        durationSteps: Math.max(.25, Math.min(total - d.n.step, d.n.durationSteps + delta))
      };else {
        const pitch = fold(noteMidi(d.n) - Math.round((e.clientY - d.y) / ROW));
        setActivePitch(pitch);
        patch = {
          step: Math.max(0, Math.min(total - d.n.durationSteps, d.n.step + delta)),
          midiNote: pitch,
          frequency: midiFrequency(pitch)
        };
      }
      update(editBassNote(d.original, d.n.id, patch, bars));
    };
    const up = () => {
      const d = drag.current;
      if (!d) return;
      drag.current = null;
      if (JSON.stringify(local.current) !== JSON.stringify(d.original)) commit(local.current);else audition(d.n);
    };
    const cancel = () => {
      if (drag.current) {
        update(drag.current.original);
        drag.current = null;
      }
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
    };
  });
  const selectedNote = draft.find(n => n.id === selected),
    index = draft.findIndex(n => n.id === selected);
  const remove = () => {
    const next = draft.filter(n => n.id !== selected);
    update(next);
    commit(next);
    setSelected(null);
  };
  const chooseSound = id => {
    preview.stop();
    const sound = catalog.find(s => s.id === id);
    onChange({
      ...value,
      sampleId: id || null,
      soundType: sound?.name || 'Sub Sine'
    });
  };
  const suggest = kind => {
    preview.stop();
    const next = kind === 'new' && onGenerate ? onGenerate(value) : bassVariation(draft, kind, bars);
    setCandidate(next);
  };
  const hearGlide = (next = draft, ms = selectedNote?.glideMs || 0) => {
    const pair = glidePreviewNotes(next, selected, ms, bars);
    if (pair) preview.play({
      ...value,
      pattern: pair,
      bars: 1
    }, bpm);
  };
  const changeGlide = ms => {
    const next = glideInto(draft, selected, ms);
    update(next);
    commit(next);
    hearGlide(next, ms);
  };
  const shiftOctave = direction => {
    const next = transposeBassOctave(draft, direction);
    update(next);
    commit(next);
    if (activePitch !== null && next !== draft) setActivePitch(activePitch + (direction < 0 ? -12 : 12));
    const pitches = next.map(noteMidi).sort((a, b) => a - b);
    if (scroll.current && pitches.length) scroll.current.scrollTop = (MAX - (pitches[0] + 4)) * ROW;
  };
  const visible = candidate || draft;
  const selectedSound = catalog.find(s => s.id === value.sampleId);
  return <section className="bass-editor" aria-label="Edit bass" onKeyDown={event => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      preview.stop();
      onClose();
    }
  }}>
    <header><img src={portrait} alt="Pizzul" /><div><h2>Edit bass</h2><span>{candidate ? 'Hear the idea, then Keep or Cancel.' : selectedNote ? 'Stretch the edge for length. Choose Glide to slide into this note.' : 'Click to draw. Home is where your bass can land.'}</span></div><button onClick={() => {
        preview.stop();
        onClose();
      }}>Close bass</button></header>
    <div className="bass-tools"><label>Sound <select aria-label="Bass sound" value={value.sampleId || value.soundType || 'Sub Sine'} disabled={!!candidate} onChange={e => {
          const v = e.target.value;
          if (['Sub Sine', 'Sawtooth Grit', 'Warm Round', 'Square Punch', 'Triangle Soft'].includes(v)) {
            preview.stop();
            onChange({
              ...value,
              sampleId: null,
              soundType: v
            });
          } else chooseSound(v);
        }}>
      <optgroup label="Synth bass">{['Sub Sine', 'Sawtooth Grit', 'Warm Round', 'Square Punch', 'Triangle Soft'].map(s => <option key={s}>{s}</option>)}</optgroup>
      {value.sampleId && !catalog.some(s => s.id === value.sampleId) && <option value={value.sampleId}>Sound unavailable</option>}
      <optgroup label="Lopez bass bank">{catalog.filter(s => !genre || s.genres.includes(genre) || s.id === value.sampleId).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</optgroup></select></label>
      <label>Genre <select aria-label="Bass genre" value={genre} onChange={e => setGenre(e.target.value)}><option value="">All genres</option>{[...new Set(catalog.flatMap(s => s.genres))].map(g => <option key={g}>{g}</option>)}</select></label>
      <label>Snap <select aria-label="Bass note snap" value={snap} onChange={e => setSnap(+e.target.value)}>{[[4, '1 beat'], [2, 'Half beat'], [1, 'Quarter beat'], [.25, 'Fine']].map(([n, l]) => <option key={n} value={n}>{l}</option>)}</select></label>
      <label><input type="checkbox" checked={lock && !!key} disabled={!key} onChange={e => {
          setLock(e.target.checked);
          onChange({
            ...value,
            lockToKey: e.target.checked
          });
        }} />Stay in key</label>
      <div className="bass-octave"><button disabled={!!candidate || !draft.length || draft.some(n => noteMidi(n) < 24)} onClick={() => shiftOctave(-1)}>Octave down</button><button disabled={!!candidate || !draft.length || draft.some(n => noteMidi(n) > 60)} onClick={() => shiftOctave(1)}>Octave up</button></div>
      <button disabled={!selectedNote || !!candidate} onClick={remove} aria-label="Delete note">Delete</button><button disabled={(onUndoEdit ? !canUndoEdit : !history.current.length) || !!candidate} onClick={undo} aria-label="Undo bass edit">Undo</button><button disabled={(onRedoEdit ? !canRedoEdit : !future.current.length) || !!candidate} onClick={redo} aria-label="Redo bass edit">Redo</button>
    </div>
    {loading && <p role="status">Loading your bass bank...</p>}{error && <p role="alert">{error} <button onClick={load}>Retry sounds</button></p>}

    <div className="bass-actions"><button onClick={() => preview.playing ? preview.stop() : preview.play({
        ...value,
        pattern: visible
      }, bpm, true)}>{preview.playing ? 'Stop bass' : 'Solo bass'}</button>{onListen && <button disabled={!!candidate} onClick={() => {
        preview.stop();
        onListen();
      }}>Hear with beat</button>}
      <label>Glide into note <select aria-label="Glide into selected note" disabled={index < 1 || !!candidate || selectedSound?.glideReady === false} value={selectedNote?.glideMs || 0} onChange={e => {
          changeGlide(+e.target.value);
        }}><option value="0">Off</option><option value="80">Short</option><option value="200">Long</option>{selectedNote?.glideMs > 0 && ![80, 200].includes(selectedNote.glideMs) && <option value={selectedNote.glideMs}>Custom ({selectedNote.glideMs} ms)</option>}</select></label>
      <button disabled={index < 1 || !!candidate || !selectedNote?.glideMs || !glidePreviewNotes(draft, selected, selectedNote?.glideMs, bars)} onClick={() => hearGlide()}>Hear glide</button>
      <details><summary>More glide</summary><label>Speed (ms) <input aria-label="Glide speed" disabled={index < 1 || !!candidate} type="number" min="0" max="500" value={selectedNote?.glideMs || 0} onChange={e => {
            const next = glideInto(draft, selected, Math.max(0, Math.min(500, +e.target.value)));
            update(next);
            commit(next);
          }} /></label></details>
    </div>
    <div className="bass-note-status" aria-live="polite"><strong>{activePitch === null ? 'Touch a piano key or select a note' : `Note: ${bassNoteName(activePitch)}`}</strong><span>{selectedNote && index < 1 ? 'Select a later note to glide into it.' : selectedNote && index > 0 && Math.abs(selectedNote.frequency - draft[index - 1].frequency) < .01 ? 'Same pitch: choose a different note to hear a pitch slide.' : selectedNote?.glideMs ? 'Glide joins the previous note. Stretch short notes for a longer slide.' : 'White and black keys show the note you are playing.'}</span></div>
    <div className="bass-guidance"><KeyLearning keySignature={keySignature} getContext={getContext} destination={destination} onStopArrangement={() => {
        preview.stop();
        onStopArrangement?.();
      }} portrait={portrait} />
    <details className="bass-help"><summary>Help me shape it</summary><div>{[['simpler', 'Simpler'], ['bounce', 'More bounce'], ['fill', 'Ending fill'], ['new', 'New variation']].filter(([k]) => k !== 'new' || onGenerate).map(([k, l]) => <button disabled={!!candidate} key={k} onClick={() => suggest(k)}>{l}</button>)}</div></details></div>
    {candidate && <div className="bass-candidate" role="status"><span>Variation preview</span><button onClick={() => {
        const n = candidate;
        update(n);
        commit(n);
      }}>Keep variation</button><button onClick={() => {
        preview.stop();
        setCandidate(null);
      }}>Cancel variation</button></div>}
    {preview.error && <p role="alert">{preview.error}</p>}
    {selectedSound && <small className="bass-tail-hint">One-shot bass: very long notes can outlast the sound. Shorten the note or choose a longer sound.</small>}
    <div className="bass-scroll" ref={scroll}><div className="bass-grid-layout" style={{
        width: width + 96,
        height: (MAX - MIN + 1) * ROW
      }}>
      <div className="bass-key-column">{Array.from({
            length: MAX - MIN + 1
          }, (_, i) => MAX - i).map(p => {
            const degree = key?.scale.indexOf(p % 12);
            const black = [1, 3, 6, 8, 10].includes(p % 12);
            return <button key={p} data-pitch={p} title={`${bassNoteName(p)}${degree === 0 ? ' · Home' : degree >= 0 ? ` · Scale step ${degree + 1}` : ''}`} className={`bass-piano-key ${black ? 'bass-black-key' : 'bass-white-key'}${degree === 0 ? ' bass-home' : ''}${activePitch === p ? ' bass-key-active' : ''}`} onClick={() => audition({
              frequency: midiFrequency(p),
              step: 0,
              durationSteps: 2,
              velocity: .8
            })} aria-label={`Hear ${bassNoteName(p)}${degree === 0 ? ', Home' : degree >= 0 ? `, step ${degree + 1}` : ''}`}><strong>{bassNoteName(p)}</strong><small>{degree === 0 ? 'Home' : degree >= 0 ? degree + 1 : ''}</small></button>;
          })}</div>
      <div className="bass-note-grid" data-testid="bass-note-grid" ref={grid} tabIndex="0" style={{
          width
        }} onPointerDown={add} onKeyDown={e => {
          if ((e.key === 'Delete' || e.key === 'Backspace') && selected && !candidate) {
            e.preventDefault();
            e.stopPropagation();
            remove();
          }
        }}>
        {Array.from({
            length: MAX - MIN + 1
          }, (_, i) => MAX - i).map((p, i) => <div key={p} className={`bass-note-row${key && !key.scale.includes(p % 12) ? ' bass-out-key' : ''}${key && p % 12 === key.root ? ' bass-home-row' : ''}${activePitch === p ? ' bass-active-row' : ''}`} style={{
            top: i * ROW
          }} />)}
        {Array.from({
            length: bars * 4 + 1
          }, (_, i) => <div key={i} className={`bass-beat-line${i % 4 === 0 ? ' bass-bar-line' : ''}`} style={{
            left: i * BEAT
          }}><span>{i % 4 === 0 ? i / 4 + 1 : ''}</span></div>)}
        {visible.map((n, i) => {
            const p = noteMidi(n),
              prev = visible[i - 1];
            return <React.Fragment key={n.id}><button data-note-id={n.id} title={`${bassNoteName(p)} · ${n.durationSteps / 4} beats`} data-pitch={p} aria-label={`Bass ${key && p % 12 === key.root ? 'Home' : names[p % 12]}, beat ${(n.step / 4 + 1).toFixed(2)}`} className={`bass-note${selected === n.id ? ' bass-selected' : ''}`} style={{
                left: n.step / 4 * BEAT,
                top: (MAX - p) * ROW + 2,
                width: Math.max(8, n.durationSteps / 4 * BEAT)
              }} onPointerDown={e => begin(e, n)} onClick={e => e.stopPropagation()}><span>{names[p % 12]}</span><span className="bass-resize" onPointerDown={e => begin(e, n, true)} /></button>{n.glideMs > 0 && prev && <svg className="bass-glide-line" style={{
                left: prev.step / 4 * BEAT,
                top: Math.min(MAX - noteMidi(prev), MAX - p) * ROW,
                width: Math.max(1, (n.step - prev.step) / 4 * BEAT),
                height: (Math.abs(p - noteMidi(prev)) + 1) * ROW
              }} aria-label="Glide connection"><line x1="0" y1={noteMidi(prev) > p ? '10' : '90%'} x2="100%" y2={noteMidi(prev) > p ? '90%' : '10'} /></svg>}</React.Fragment>;
          })}
      </div></div></div>
    <small>Drag a note to move it. Drag its right edge for length. Select and press Delete to remove.</small>
  </section>;
}
